import React, { useRef, useState } from "react";
import upload from "../../assets/svg/upload.svg";
import { X } from "lucide-react"; // Close icon

export const FileUploader = ({
  onFileSelect,
  label = "Upload file",
  multiple = false,
  editImg = [],
  onRemoveEdit,
}) => {
  const fileInputRef = useRef(null);
  const [filePreviews, setFilePreviews] = useState([]);

  const handleInputFile = () => {
    fileInputRef.current.click();
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);

    const filePromises = fileArray.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ file, base64: reader.result });
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file); // Convert to base64
      });
    });

    Promise.all(filePromises)
      .then((fileData) => {
        const newFiles = fileData.map(({ file, base64 }) => ({
          file,
          preview: URL.createObjectURL(file),
          base64, // Add base64 representation
        }));

        setFilePreviews((prev) => {
          let updated;
          if (multiple) {
            updated = [...prev, ...newFiles];
          } else {
            updated = [newFiles[0]];
          }

          // Send base64 strings to the parent
          onFileSelect(updated.map((f) => f.base64));

          return updated;
        });
      })
      .catch((error) => {
        console.error("Error reading files:", error);
      });
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const removeFile = (index) => {
    if (filePreviews.length > 0) {
      // Removing from newly selected files
      setFilePreviews((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        onFileSelect(updated.map((f) => f.file)); // Update parent with remaining files
        return updated;
      });
    } else if (editImg.length > 0) {
      onRemoveEdit();
    }
  };

  return (
    <div className="col-xl-12 mt-2">
      {/* Title */}
      <p className="fs-16 fw-600" style={{ color: "#172B4D" }}>
        {label}
      </p>

      {/* Preview for single or multiple */}
      {filePreviews.length > 0 && multiple && (
        <div
          className={`d-flex gap-2 flex-wrap mb-3 ${
            !multiple ? "justify-content-start" : ""
          }`}
        >
          {filePreviews.map((fileObj, index) => (
            <div key={index} style={{ position: "relative" }}>
              <img
                src={fileObj.preview}
                alt={`preview-${index}`}
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              <button
                onClick={() => removeFile(index)}
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  background: "white",
                  border: "none",
                  borderRadius: "50%",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                <X size={16} color="#7157C2" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <div
        onClick={handleInputFile}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDragLeave={(e) => e.preventDefault()}
        className="p-3 d-flex flex-column gap-2 align-items-center justify-content-center rounded-4"
        style={{
          border: "1px dashed #7157C2",
          backgroundColor: "#F8F5FF",
          cursor: "pointer",
        }}
      >
        {!multiple && (filePreviews.length > 0 || editImg.length > 0) ? (
          <div key={0} style={{ position: "relative", marginBottom: "1rem" }}>
            <img
              src={
                filePreviews.length > 0 ? filePreviews[0].preview : editImg[0]
              }
              alt={`preview-0`}
              style={{
                width: "80px",
                height: "80px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation(); // prevent bubbling
                removeFile(0);
              }}
              style={{
                position: "absolute",
                top: "-5px",
                right: "-5px",
                background: "white",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <X size={16} color="#7157C2" />
            </button>
          </div>
        ) : (
          <>
            <div
              className="p-3 rounded-4"
              style={{ backgroundColor: "#EBE4FF" }}
            >
              <img src={upload} alt="upload" />
            </div>
            <p className="m-0" style={{ color: "#8790A1" }}>
              Drag and drop to upload or
            </p>
            <p
              className="text-decoration-underline"
              style={{ color: "#7157C2" }}
            >
              Click here to browse
            </p>
          </>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="d-none"
        multiple={multiple}
        onChange={handleFileChange}
      />
    </div>
  );
};
